import { signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { IndicatorDataState } from './indicator-data.state';
import { DataEntry, IndicatorData } from '../../models/indicator-data';
import { ComparisonValueStore } from '../comparison-value/comparison-value.state';
import { inject } from '@angular/core';
import { makeUniqueStringArray } from '../../utility/array.utility';

export type DataGroupProperty = 'feature' | 'indicator' | 'dataLayer';

export interface GroupedDataFilter {
  indicator?: string;
  dataLayer?: string;
  indicators: string[];
  disabledDataLayers: string[];
  disabledYears: number[];
  disabledFeatures: string[];
  dataLayerType: string;
  availableFeatureKeys: string[];
  comparisonKeys: string[];
}

const filterItems = (
  items: Record<string, IndicatorData>,
  filterOptions: GroupedDataFilter,
  focusProperty: 'indicator' | 'dataLayer' | 'feature',
  focusKey: string
): IndicatorData[] => {
  let data: IndicatorData[];
  if (focusProperty === 'indicator') {
    data = Object.values(items).filter(item => {
      return !(
        item.indicator !== focusKey ||
        filterOptions.dataLayerType !== item.dataLayerType ||
        filterOptions.disabledDataLayers.includes(item.dataLayer)
      );
    });
  } else if (focusProperty === 'dataLayer') {
    data = Object.values(items).filter(item => {
      return !(
        item.dataLayer !== focusKey ||
        !filterOptions.indicators.includes(item.indicator) ||
        filterOptions.dataLayerType !== item.dataLayerType
      );
    });
  } else {
    data = Object.values(items).filter(item => {
      if (
        !filterOptions.indicators.includes(item.indicator) ||
        filterOptions.dataLayerType !== item.dataLayerType
      )
        return false;
      return !filterOptions.disabledDataLayers.includes(item.dataLayer);
    });
  }

  if (focusProperty === 'indicator') return data;
  // Sort the filtered data by the order of indicators in filterOptions.indicators
  const indicatorOrderMap = new Map(
    filterOptions.indicators.map((indicator, index) => [indicator, index])
  );

  return data.sort((a, b) => {
    const orderA =
      indicatorOrderMap.get(a.indicator) ?? Number.MAX_SAFE_INTEGER;
    const orderB =
      indicatorOrderMap.get(b.indicator) ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });
};

const getValidDistrictKeys = (
  itemData: DataEntry,
  disabledFeatures: string[],
  availableKeys: string[]
): string[] => {
  //return Object.keys(itemData).filter(
  return availableKeys.filter(
    key => !disabledFeatures.includes(key) /* && availableKeys.includes(key)*/
  );
};

const getValidYears = (
  disabledYears: string[],
  availableYears: string[]
): string[] => {
  //return Object.keys(itemData).filter(
  return availableYears.filter(
    years => !disabledYears.includes(years) /* && availableKeys.includes(key)*/
  );
};

export interface GroupedDataItem {
  value: number | null;
  unit: string;
  decimals: number;
}

// Helper to initialize or retrieve nested objects
function getOrInitialize<T>(
  obj: Record<string, any>,
  key: string,
  initValue: any = {}
): T {
  obj[key] = obj[key] || initValue;
  return obj[key];
}

export function withWidgetSelectors() {
  return signalStoreFeature(
    {
      state: type<IndicatorDataState>(),
    },
    withMethods((store, comparisonStore = inject(ComparisonValueStore)) => ({
      /**
       * Group by properties with filtering
       * @param focusKey The key filtered by prop1
       * @param focusProperty The first-level group
       * @param groupProperty The second-level group
       * @param datasetProperty The second-level group
       * @param filterOptions Filter options
       */
      groupByProperties(
        focusKey: string,
        focusProperty: DataGroupProperty,
        groupProperty: DataGroupProperty,
        datasetProperty: DataGroupProperty,
        filterOptions: GroupedDataFilter
      ): {
        dataLayerIds: string[];
        features: Record<string, Record<string, number | null>>;
        comparison: Record<string, Record<string, number | null>>;
      } {
        const items = filterItems(
          store.items(),
          filterOptions,
          focusProperty,
          focusKey
        );
        const result: Record<string, Record<string, number | null>> = {};
        const comparisonResult: Record<string, Record<string, any>> = {};
        const dataLayerIds = new Set<string>(); // Use a Set to avoid duplicates
        const comparisonValues = comparisonStore.asArray();
        const allComparisonKeys = Object.keys(comparisonStore.byKey());

        items.forEach(item => {
          const itemData: DataEntry = {
            ...item.data,
            avg: item.avg ?? null,
            total: item.total ?? null,
          };

          const districtKeys = getValidDistrictKeys(
            itemData,
            filterOptions.disabledFeatures,
            [...filterOptions.availableFeatureKeys, ...allComparisonKeys]
          );

          const layerId = item.dataLayer;
          const indicatorId = item.indicator;
          dataLayerIds.add(layerId);

          if (focusProperty === 'feature') {
            handleFeatureFocus(
              item,
              result,
              comparisonResult,
              groupProperty,
              datasetProperty,
              itemData,
              focusKey,
              comparisonValues
            );
          } else if (
            focusProperty === 'indicator' &&
            indicatorId === focusKey
          ) {
            handleIndicatorFocus(
              layerId,
              districtKeys,
              groupProperty,
              itemData,
              result,
              comparisonValues,
              comparisonResult,
              filterOptions
            );
          } else if (focusProperty === 'dataLayer' && layerId === focusKey) {
            handleDataLayerFocus(
              indicatorId,
              districtKeys,
              groupProperty,
              itemData,
              result,
              comparisonValues,
              comparisonResult,
              filterOptions
            );
          }
        });

        return {
          dataLayerIds: Array.from(dataLayerIds),
          features: result,
          comparison: comparisonResult,
        };
      },
    }))
  );
}

// === Supporting Methods Using getOrInitialize ===

function handleFeatureFocus(
  item: any,
  result: Record<string, Record<string, number | null>>,
  comparisonResult: Record<string, Record<string, any>>,
  groupProperty: string,
  datasetProperty: string,
  itemData: DataEntry,
  focusKey: string,
  comparisonValues: any[]
) {
  const key1 = item[groupProperty];
  const key2 = item[datasetProperty];
  if (!key1 || !key2) return;

  const featureResult = getOrInitialize<Record<string, number | null>>(
    result,
    key2
  );
  featureResult[key1] = itemData[focusKey] ?? null;

  comparisonValues.forEach(comparisonValue => {
    const compKeyResult = getOrInitialize<Record<string, any>>(
      comparisonResult,
      comparisonValue.key
    );
    const compResult = getOrInitialize<Record<string, any>>(
      compKeyResult,
      key2
    );
    compResult[key1] = itemData[comparisonValue.key] || {};
  });
}

function handleIndicatorFocus(
  layerId: string,
  districtKeys: string[],
  groupProperty: string,
  itemData: DataEntry,
  result: Record<string, Record<string, number | null>>,
  comparisonValues: any[],
  comparisonResult: Record<string, Record<string, any>>,
  filterOptions: GroupedDataFilter
) {
  if (groupProperty === 'feature') {
    const layerResult = getOrInitialize<Record<string, number | null>>(
      result,
      layerId
    );

    districtKeys.forEach(districtKey => {
      layerResult[districtKey] = itemData[districtKey] ?? null;
    });

    comparisonValues.forEach(comparisonValue => {
      const data: Record<string, any> = districtKeys.reduce(
        (acc: Record<string, any>, districtKey) => {
          acc[districtKey] = itemData[comparisonValue.key] ?? null;
          return acc;
        },
        {}
      );

      const compResult = getOrInitialize<Record<string, any>>(
        comparisonResult,
        comparisonValue.key
      );
      compResult[layerId] = data;
    });
  } else if (groupProperty === 'dataLayer') {
    districtKeys.forEach(districtKey => {
      const districtResult = getOrInitialize<Record<string, number | null>>(
        result,
        districtKey
      );
      districtResult[layerId] = itemData[districtKey] ?? null;
    });

    comparisonValues.forEach(comparisonValue => {
      const compResult = getOrInitialize<Record<string, any>>(
        comparisonResult,
        comparisonValue.key
      );
      compResult[layerId] = itemData[comparisonValue.key] ?? null;
    });
  }
}

function handleDataLayerFocus(
  indicatorId: string,
  districtKeys: string[],
  groupProperty: string,
  itemData: DataEntry,
  result: Record<string, Record<string, number | null>>,
  comparisonValues: any[],
  comparisonResult: Record<string, Record<string, any>>,
  filterOptions: GroupedDataFilter
) {
  if (groupProperty === 'feature') {
    const indicatorResult = getOrInitialize<Record<string, number | null>>(
      result,
      indicatorId
    );

    districtKeys.forEach(districtKey => {
      indicatorResult[districtKey] = itemData[districtKey] ?? null;
    });

    comparisonValues.forEach(comparisonValue => {
      const data: Record<string, any> =
        filterOptions.availableFeatureKeys.reduce(
          (acc: Record<string, any>, districtKey) => {
            acc[districtKey] = itemData[comparisonValue.key] ?? null;
            return acc;
          },
          {}
        );

      const compResult = getOrInitialize<Record<string, any>>(
        comparisonResult,
        comparisonValue.key
      );
      compResult[indicatorId] = data;
    });
  } else if (groupProperty === 'indicator') {
    districtKeys.forEach(districtKey => {
      const districtResult = getOrInitialize<Record<string, number | null>>(
        result,
        districtKey
      );
      districtResult[indicatorId] = itemData[districtKey] ?? null;
    });

    comparisonValues.forEach(comparisonValue => {
      const compResult = getOrInitialize<Record<string, any>>(
        comparisonResult,
        comparisonValue.key
      );
      compResult[indicatorId] = itemData[comparisonValue.key] ?? null;
    });
  }
}
